import { ReactElement } from 'react';

import { Box } from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import { EllipsisTooltip } from '@/components';
import {
  excludeValues,
  formatDate,
  sortStrings,
} from '@/components/DataGrid/utils';
import { ASSIGNMENT_STATUS } from '@/constants';
import { AssignmentStatus, IAddress, IAddressInfo, IUser } from '@/models';
import { LinkedCasesCell } from '@/pages/CFS/components/LinkedCasesCell';
import { getAddressInfoLocation } from '@/services';
import { formatPersonName } from '@/utils';

import { CFSLocationFilterInput } from './components/CFSLocationFilterInput';
import { CFSNamesFilterInput } from './components/CFSNamesFilterInput';

const number = (): Partial<GridColDef> => ({
  renderCell: (params: GridRenderCellParams) => (
    <Box className="block">{params.row.number}</Box>
  ),
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
});

const createdBy = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const { firstName, lastName } = params.row.createdBy as IUser;
    return formatPersonName({ firstName, lastName });
  },
  sortComparator: (a, b) => sortStrings(a?.fullName, b?.fullName),
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
});

const receivedAt = (): Partial<GridColDef> => ({
  renderCell: (params) => (
    <Box className="block">
      {formatDate(params.row.receivedAt || params.row.createdAt)}
    </Box>
  ),
  filterOperators: getGridDateOperators(true).filter(
    excludeValues(['isNotEmpty', 'isEmpty']),
  ),
});

const incidentCodes = (): Partial<GridColDef> => ({
  renderCell: (params: any) => {
    return (
      <Box
        sx={{
          width: params.colDef.width,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <EllipsisTooltip style={{ fontSize: '0.875rem' }}>
          {params.value || ''}
        </EllipsisTooltip>
      </Box>
    );
  },
  minWidth: 260,
  filterable: false,
});

const disposition = (): Partial<GridColDef> => ({
  renderCell: (params) => (
    <Box className="block">{params.row.disposition || ''}</Box>
  ),
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
});

const assignedAgencies = (): Partial<GridColDef> => ({
  renderCell: (params: any) => {
    return (
      <Box
        sx={{
          width: params.colDef.width,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <EllipsisTooltip style={{ fontSize: '0.875rem' }}>
          {params.value || ''}
        </EllipsisTooltip>
      </Box>
    );
  },
  minWidth: 200,
  filterable: false,
});

const linkedCases = (): Partial<GridColDef> => ({
  renderCell: (params: GridRenderCellParams) => (
    <LinkedCasesCell cases={params.row?.linkedCases} />
  ),
  sortable: false,
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
});

const linkedNames = (): Partial<GridColDef> => ({
  renderCell: (params: any) => {
    return (
      <Box
        sx={{
          width: params.colDef.width,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <EllipsisTooltip
          style={{ fontSize: '0.875rem', textTransform: 'uppercase' }}
        >
          {params.value || ''}
        </EllipsisTooltip>
      </Box>
    );
  },
  minWidth: 200,
  filterOperators: getGridStringOperators()
    .filter((operator) => operator.value === 'isAnyOf')
    .map((operator) => ({
      ...operator,
      label: 'Is',
      InputComponent: CFSNamesFilterInput,
    })),
  sortable: false,
});

const addressInfo = (): Partial<GridColDef> => ({
  field: 'addressInfo',
  renderCell: (params) => (
    <Box
      className="block"
      sx={{
        width: params.colDef.width,
        display: 'flex',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <EllipsisTooltip style={{ fontSize: '0.875rem' }}>
        {getAddressInfoLocation(params.row.addressInfo)}
      </EllipsisTooltip>
    </Box>
  ),
  filterOperators: (() => {
    const baseOperators = getGridStringOperators().filter(
      excludeValues([
        'isAnyOf',
        'isNotEmpty',
        'isEmpty',
        'equals',
        'doesNotEqual',
      ]),
    );
    const equalsOperator = getGridStringOperators().find(
      (operator) => operator.value === 'equals',
    );
    const isOperator = equalsOperator
      ? { ...equalsOperator, label: 'Is' }
      : null;
    const operators = isOperator
      ? [...baseOperators, isOperator]
      : baseOperators;

    return operators.map((operator) => ({
      ...operator,
      InputComponent: CFSLocationFilterInput,
    }));
  })(),
  valueGetter: (value: { address: IAddress }) => {
    return getAddressInfoLocation(value as IAddressInfo);
  },
  sortComparator: (a, b) => {
    return sortStrings(a, b);
  },
  minWidth: 260,
});

const shortDescription = (): Partial<GridColDef> => ({
  renderCell: (params) => (
    <Box className="block uppercase">{params.formattedValue}</Box>
  ),
  filterOperators: getGridStringOperators().filter(
    excludeValues(['equals', 'doesNotEqual', 'isAnyOf']),
  ),
  sortComparator: (a, b) => {
    return sortStrings(a, b);
  },
});

const cfsStatus = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    if (params.row?.isClosed) {
      return <Box>Closed</Box>;
    }

    if (params.row?.fireControlled) {
      return <Box>Fire Controlled</Box>;
    }

    if (
      params.row?.isReadyToDispatch &&
      params.row?.assignmentStatus === AssignmentStatus.NEW
    ) {
      return <Box>Ready for dispatch</Box>;
    }

    const status = params?.row?.assignmentStatus as AssignmentStatus;
    return (
      <Box style={{ display: 'block' }}>
        {status ? ASSIGNMENT_STATUS[status]?.label : null}
      </Box>
    );
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['equals', 'doesNotEqual', 'isAnyOf']),
  ),
  sortComparator: (a, b) => sortStrings(a?.normalized, b?.normalized),
});

const action = (
  actionNode?: (params: GridRenderCellParams) => ReactElement,
): Partial<GridColDef> => ({
  filterable: false,
  sortable: false,
  resizable: false,
  renderCell: actionNode,
});

export const cfsCols = (
  actionNode?: (params: GridRenderCellParams) => ReactElement,
) => {
  return {
    number,
    createdBy,
    receivedAt,
    incidentCodes,
    disposition,
    addressInfo,
    linkedCases,
    linkedNames,
    assignedAgencies,
    shortDescription,
    cfsStatus,
    action: () => action(actionNode),
  };
};
